import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { 
    CheckCircle2, 
    AlertCircle, 
    ArrowLeft,
    MessageSquare,
    ExternalLink,
    X
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { cn } from '@/lib/utils';

interface WhatsAppOnboardingFlowProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete: (proceed: boolean) => void;
}

type OnboardingStep = 'facebook-question' | 'whatsapp-usage' | 'account-notice' | 'phone-notice' | 'ready';

interface StepData {
    id: OnboardingStep;
    title: string;
    question?: string;
    description?: string;
    notice?: string;
    yesLabel?: string;
    noLabel?: string;
    steps?: string[];
    requirements?: string[];
    timeline?: string;
    nextStep?: {
        yes: OnboardingStep;
        no: OnboardingStep;
    };
}

const steps: Record<OnboardingStep, StepData> = {
    'facebook-question': {
        id: 'facebook-question',
        title: 'Facebook Account',
        question: 'Do you have a personal Facebook account?',
        yesLabel: 'Yes',
        noLabel: 'No',
        nextStep: {
            yes: 'whatsapp-usage',
            no: 'account-notice'
        }
    },
    'whatsapp-usage': {
        id: 'whatsapp-usage',
        title: 'WhatsApp Business',
        question: 'Do you already use WhatsApp for your business as an app on your smartphone?',
        yesLabel: 'Yes',
        noLabel: 'No',
        nextStep: {
            yes: 'phone-notice',
            no: 'ready'
        }
    },
    'account-notice': {
        id: 'account-notice',
        title: 'Facebook Account Required',
        notice: 'You need a Facebook account. This must be registered in your name. During onboarding, you will then create a Business account that is linked to your personal account.',
        description: 'Click here to create an account.',
        nextStep: {
            yes: 'whatsapp-usage',
            no: 'whatsapp-usage'
        }
    },
    'phone-notice': {
        id: 'phone-notice',
        title: 'Phone Number Migration',
        notice: 'Your phone number to be registered is already being used in the WhatsApp (Business) app. The number must first be released. To do this, delete your existing account as follows:',
        description: 'Open App ▸ Settings ▸ Account ▸ Delete Account',
        nextStep: {
            yes: 'ready',
            no: 'ready'
        }
    },

    'ready': {
        id: 'ready',
        title: 'Ready to Start',
        description: 'Great! You are ready to start.'
    }
};

export const WhatsAppOnboardingFlow = ({ 
    isOpen, 
    onClose, 
    onComplete
}: WhatsAppOnboardingFlowProps) => {
    const [currentStep, setCurrentStep] = useState<OnboardingStep>('facebook-question');
    const [stepHistory, setStepHistory] = useState<OnboardingStep[]>([]);

    
    // Reset flow when dialog opens/closes
    const resetFlow = () => {
        setCurrentStep('facebook-question');
        setStepHistory([]);
    };
    
    // Reset when dialog closes
    const handleClose = () => {
        resetFlow();
        onClose();
    };
    
    const step = steps[currentStep];
    
    const handleAnswer = (answer: 'yes' | 'no') => {
        if (step.nextStep) {
            const nextStepId = step.nextStep[answer];
            setStepHistory(prev => [...prev, currentStep]);
            setCurrentStep(nextStepId);
        }
    };
    

    
    const handleBack = () => {
        if (stepHistory.length > 0) {
            const previousStep = stepHistory[stepHistory.length - 1];
            setStepHistory(prev => prev.slice(0, -1));
            setCurrentStep(previousStep);
        }
    };
    
    const handleProceed = () => {
        resetFlow(); // Reset the flow before proceeding
        onComplete(true);
    };
    

    
    const containerVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: { 
                type: "spring",
                stiffness: 300,
                damping: 25
            }
        },
        exit: { 
            opacity: 0, 
            scale: 0.9,
            transition: { duration: 0.2 }
        }
    };
    
    const stepVariants = {
        hidden: { opacity: 0, x: 20 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: { duration: 0.3 }
        },
        exit: { 
            opacity: 0, 
            x: -20,
            transition: { duration: 0.2 }
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className={cn(
                        "w-full",
                        currentStep === 'ready' ? "max-w-4xl" : "max-w-2xl"
                    )}
                >
                    <Card className="p-0 overflow-hidden max-h-[95vh] flex flex-col shadow-xl">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950/40 dark:to-green-900/40 border-b dark:border-green-800/30 px-4 py-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="h-8 w-8 rounded-lg bg-green-600 dark:bg-green-600 flex items-center justify-center shadow-sm">
                                        <WhatsAppIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">WhatsApp Onboarding</h2>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleClose}
                                    className="h-8 w-8 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        
                        {/* Progress Indicator */}
                        {currentStep !== 'ready' && (
                            <div className="px-6 py-3 border-b bg-muted/30">
                                <div className="flex items-center space-x-2">
                                    <div className="text-sm text-muted-foreground">
                                        Before we get started, please answer a few questions to prepare.
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Content */}
                        <div className="p-6 flex-1 overflow-y-auto min-h-[300px]">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentStep}
                                    variants={stepVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="space-y-6"
                                >
                                    {/* Step Icon */}
                                    <div className="flex items-center space-x-3 mb-6">
                                        <div className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center shadow-sm",
                                            currentStep === 'ready' 
                                                ? "bg-green-100 dark:bg-green-900/30" 
                                                : "bg-blue-100 dark:bg-blue-900/30"
                                        )}>
                                            {currentStep === 'ready' ? (
                                                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            ) : step.notice ? (
                                                <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            ) : (
                                                <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            )}
                                        </div>
                                        <h3 className="text-lg font-semibold">
                                            {step.title}
                                        </h3>
                                    </div>
                                    
                                    {/* Question */}
                                    {step.question && (
                                        <div className="space-y-4">
                                            <p className="text-base leading-relaxed">
                                                {step.question}
                                            </p>
                                            <div className="flex space-x-3">
                                                <Button
                                                    onClick={() => handleAnswer('yes')}
                                                    variant="outline"
                                                    size="sm"
                                                >
                                                    {step.yesLabel}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleAnswer('no')}
                                                >
                                                    {step.noLabel}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    
                                    {/* Notice for other steps */}
                                    {step.notice && (
                                        <div className="space-y-4">
                                            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4">
                                                <p className="text-blue-800 dark:text-blue-300 text-sm leading-relaxed">
                                                    {step.notice}
                                                </p>
                                                {step.description && (
                                                    <div className="mt-3">
                                                        {currentStep === 'account-notice' ? (
                                                            <Button
                                                                variant="link"
                                                                className="p-0 h-auto text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                                onClick={() => window.open('https://facebook.com/', '_blank')}
                                                            >
                                                                {step.description}
                                                                <ExternalLink className="w-3 h-3 ml-1" />
                                                            </Button>
                                                        ) : (
                                                            <p className="text-blue-700 dark:text-blue-400 text-sm font-medium">
                                                                {step.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                {currentStep === 'phone-notice' && (
                                                    <div className="mt-3 text-xs text-blue-600 dark:text-blue-400">
                                                        <p><strong>Note:</strong> Existing conversations will not be transferred.</p>
                                                        <p>However, nothing changes for your customers.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Ready State */}
                                    {currentStep === 'ready' && (
                                        <div className="space-y-6">
                                            <div className="border-2 border-dashed border-green-200 dark:border-green-800/50 bg-green-50/30 dark:bg-green-950/20 rounded-lg p-8">
                                                <div className="flex justify-center">
                                                    <Button
                                                        onClick={handleProceed}
                                                        className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white px-8"
                                                    >
                                                        Start WhatsApp Onboarding
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* FAQ Section */}
                                            <div className="mt-8">
                                                <h3 className="text-lg font-semibold mb-4">Frequently Asked Questions</h3>
                                                <Accordion type="single" collapsible className="w-full [&>div:last-child]:border-b-0">
                                                    <AccordionItem value="item-0" className="border-border">
                                                        <AccordionTrigger className="text-left hover:no-underline">
                                                            What do I need to get started with WhatsApp Business?
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            <div className="space-y-3 text-sm text-muted-foreground">
                                                                <p>To use WhatsApp Business messaging through our platform, you'll need to have the following prepared:</p>
                                                                <p className="font-medium text-foreground">Requirements:</p>
                                                                <ul className="list-disc list-inside space-y-1 ml-2">
                                                                    <li>A verified Meta Business Manager account</li>
                                                                    <li>Business email required (no free domains like Gmail/Outlook)</li>
                                                                    <li>Business phone number (not linked to existing WhatsApp)</li>
                                                                    <li>Business verification documents (if required by Meta)</li>
                                                                </ul>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                    <AccordionItem value="item-1" className="border-border">
                                                        <AccordionTrigger className="text-left hover:no-underline">
                                                            My account has been restricted
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            <div className="space-y-3 text-sm text-muted-foreground">
                                                                <p>Facebook has strict but very opaque rules for its platform. It regularly happens that during onboarding the Facebook account gets blocked or restricted.</p>
                                                                <p>In this case, verification is necessary. You may need to submit a photo of your ID for verification. Click on the <strong className="text-foreground">Facebook Account Status</strong> button under "Useful Links" on this page.</p>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                    <AccordionItem value="item-2" className="border-border">
                                                        <AccordionTrigger className="text-left hover:no-underline">
                                                            Messages are not being delivered
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            <div className="space-y-3 text-sm text-muted-foreground">
                                                                <p>Your WhatsApp account was probably blocked. Read the section "My account has been restricted" on this page.</p>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                    <AccordionItem value="item-3" className="border-border">
                                                        <AccordionTrigger className="text-left hover:no-underline">
                                                            How long does WhatsApp Business verification take?
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            <div className="space-y-3 text-sm text-muted-foreground">
                                                                <p className="font-medium text-foreground">Typical Timeline:</p>
                                                                <ul className="list-disc list-inside space-y-1 ml-2">
                                                                    <li>Meta Business Manager connection: 10-15 minutes</li>
                                                                    <li>Phone number registration: 15 minutes</li>
                                                                    <li>Number verification: Usually immediate</li>
                                                                    <li>Business verification (if required): 3-14 business days</li>
                                                                </ul>
                                                                <p><strong className="text-foreground">Note:</strong> Basic setup typically takes around 30 minutes. Once verified, you can start using WhatsApp messaging.</p>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                    <AccordionItem value="item-4" className="border-border">
                                                        <AccordionTrigger className="text-left hover:no-underline">
                                                            I get an error when connecting to Facebook
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            <div className="space-y-3 text-sm text-muted-foreground">
                                                                <p>Your Facebook account may have originally been set up with a phone number instead of an email address. Please try using an alternative method or contact support for assistance.</p>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                    <AccordionItem value="item-5" className="border-border">
                                                        <AccordionTrigger className="text-left hover:no-underline">
                                                            Useful Links
                                                        </AccordionTrigger>
                                                        <AccordionContent>
                                                            <div className="space-y-3">
                                                                <div className="grid grid-cols-1 gap-3">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="justify-start"
                                                                        onClick={() => window.open('https://business.facebook.com/accountquality', '_blank')}
                                                                    >
                                                                        <ExternalLink className="w-4 h-4 mr-2" />
                                                                        Facebook Account Status
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="justify-start"
                                                                        onClick={() => window.open('https://business.facebook.com/settings', '_blank')}
                                                                    >
                                                                        <ExternalLink className="w-4 h-4 mr-2" />
                                                                        Facebook Business Manager
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="justify-start"
                                                                        onClick={() => window.open('https://accountscenter.facebook.com/password_and_security/two_factor', '_blank')}
                                                                    >
                                                                        <ExternalLink className="w-4 h-4 mr-2" />
                                                                        Facebook 2FA / 2FA Direct Link
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="justify-start"
                                                                        onClick={() => window.open('https://business.facebook.com/wa/manage/home', '_blank')}
                                                                    >
                                                                        <ExternalLink className="w-4 h-4 mr-2" />
                                                                        WhatsApp Manager
                                                                    </Button>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground space-y-1">
                                                                    <p><strong className="text-foreground">Facebook Account Status:</strong> Check here if your account has been restricted. You will usually also receive a corresponding email.</p>
                                                                    <p><strong className="text-foreground">Facebook Business Manager:</strong> Here you can manage your Facebook Business account.</p>
                                                                    <p><strong className="text-foreground">Facebook 2FA:</strong> Here you can set up two-factor authentication.</p>
                                                                    <p><strong className="text-foreground">WhatsApp Manager:</strong> All settings related to WhatsApp can be managed here directly.</p>
                                                                </div>
                                                            </div>
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                </Accordion>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                        
                        {/* Footer */}
                        {stepHistory.length > 0 && (
                            <div className="border-t bg-muted/20 px-6 py-2 flex-shrink-0">
                                <div className="flex items-center">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleBack}
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
